// TarotResultPage.jsx
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const GOLD = "#FFD86E";

function InfoBlock({ icon, title, children }) {
  return (
    <section className="mt-5">
      <div className="relative rounded-[22px] p-[1.5px]">
        <div
          className="rounded-[22px] border border-white/60 px-5 sm:px-6 py-5 sm:py-6"
          style={{ background: "rgba(255,255,255,0.02)" }}
        >
          <h3 className="flex items-center gap-2 text-[18px] sm:text-[20px] font-extrabold">
            <span className="inline-flex w-6 h-6 items-center justify-center">
              {icon}
            </span>
            {title}
          </h3>
          <p className="text-[14px] sm:text-[15px] leading-6 mt-2 text-white/90 text-left">
            {children}
          </p>
        </div>
      </div>
    </section>
  );
}

export default function TarotResultPage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // ✅ เปิดเพจแล้วสกรอลไปบนสุดเสมอ
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"; // ปิด auto-restore ของเบราว์เซอร์
    }
    // ใช้ rAF ให้ทำหลัง DOM วาดเสร็จ
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "view_tarot_love_card_result",
      category: "Tarot",
      label: "view_tarot_love_card_result",
    });
  }, []);

  // ตัวอย่าง fallback (เพิ่ม love เพื่อเดโม)
  const fallback = [
    {
      id: 64,
      card: "ไพ่ Wands (ไม้เท้า)",
      image: "/images/tarot/aceofwands.jpeg",
      text: "หยุดพัก… และเฉลิมฉลองความสำเร็จของคุณ ไพ่ใบนี้ชี้ถึงไฟเริ่มต้น พลัง และความกล้าลงมือทำ",
      prediction:
        "สัญลักษณ์ของการเริ่มต้น ความมุ่งมั่น และการลงมือทำอย่างมีพลัง พร้อมพาโอกาสใหม่เข้ามา",
      love: {
        single:
          "มีลุ้นเริ่มต้นความสัมพันธ์จากโปรเจ็กต์/งานใหม่ หรือกิจกรรมที่คุณลงมือทำ",
        couple:
          "จุดไฟเป้าหมายร่วมกัน วางแผนเรื่องใหญ่แล้วลงมือจริง ความสัมพันธ์คึกคักขึ้น",
      },
      tags: ["ความมั่นคง", "ความสำเร็จ", "ความสุข", "ความอบอุ่น"],
    },
  ];

  const cards = state?.cards?.length ? state.cards : fallback;
  const card = cards[0]; // หน้าเดโมแสดงใบแรกที่เลือก

  // === NEW: รวมความหมายความรัก (single/couple) ใส่ลงใน card.prediction แบบ JSX ===
  const predictionNode = card?.love ? (
    <>
      <span className="font-semibold">คนโสด :</span> {card.love.single}
      <br />
      <br />
      <span className="font-semibold">คนที่มีคู่รักแล้ว :</span>{" "}
      {card.love.couple}
    </>
  ) : (
    card?.prediction ||
    "เป็นสัญลักษณ์ของความสำเร็จ ความมั่นคง และความอุ่นใจ พร้อมพาโอกาสดี ๆ เข้ามา"
  );

  return (
    <div className="min-h-screen bg-[#0A0F28] text-white">
      {/* Header: Back */}
      <header
        className="relative z-10"
        style={{
          position: "absolute",
          paddingTop: "calc(env(safe-area-inset-top,0px) + 10px)",
        }}
      >
        <div className="w-full h-16 px-5 py-2 flex items-end">
          <button
            aria-label="ย้อนกลับ"
            onClick={() => navigate("/horoscope/tarot")}
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl
                       bg-white/10 hover:bg-white/15 active:bg-white/20
                       shadow-[0_4px_14px_rgba(0,0,0,0.35)] backdrop-blur-[2px]
                       focus:outline-none focus:ring-2 focus:ring-white/30"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 19L8 12L15 5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="px-6 pt-16 pb-16 max-w-[720px] mx-auto">
        {/* รูปไพ่ */}
        <div className="flex justify-center mt-4">
          <div className="rounded-[18px] overflow-hidden shadow-[0_14px_36px_rgba(0,0,0,0.45)]">
            <img
              src={card.image}
              alt={card.card}
              className="w-[180px] sm:w-[200px] h-auto object-cover"
            />
          </div>
        </div>
        {/* ชื่อไพ่ */}
        <h1 className="text-center mt-6 text-[22px] sm:text-[26px] font-extrabold">
          {card.card}
        </h1>
        {/* แท็ก */}
        {/*<div className="flex flex-wrap justify-center gap-2 mt-3">
          {(card.tags ?? ["ความรัก", "ความสำเร็จ"]).map((t, idx) => (
            <span
              key={idx}
              className="px-3 py-1.5 rounded-full text-[13px] border border-white/50 text-white/90"
            >
              {t}
            </span>
          ))}
        </div>*/}
        <InfoBlock
          title="ความหมายของไพ่"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path
                d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27Z"
                stroke="white"
                strokeWidth="1.6"
              />
            </svg>
          }
        >
          {card.text}
        </InfoBlock>
        <InfoBlock
          title="คำทำนายจากไพ่"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path
                d="M1.5 12s4-6.5 10.5-6.5S22.5 12 22.5 12 18.5 18.5 12 18.5 1.5 12 1.5 12Z"
                stroke="white"
                strokeWidth="1.6"
              />
              <circle cx="12" cy="12" r="3" fill="white" />
            </svg>
          }
        >
          {predictionNode}
        </InfoBlock>
      </main>
    </div>
  );
}
